// AdminPanel — the workspace-data-export Settings section, pure.
//
// Realizes the surviving half of the visual spec at
// sketches/admin/AdminView.tsx (IDD b3df0043): the export row. The rest of
// that sketch's scope has been folded away —
//   - "Delete workspace" merged into the ONE delete action on Account
//     (`Iam/Account/Account.tsx`'s "Delete account", which already fires the
//     identical `POST /account/delete` cascade) — see
//     .claude/analysis/settings-simplification-2026-09-22.md §2/§3. No second
//     button, no second confirm dialog.
//   - Integrations (Torus/Canvas "Connect") deleted entirely — zero backing
//     state ever existed (no connector storage), so the section was a
//     permanently-disabled placeholder. Re-add the day a connector actually
//     authenticates against something.
//
// Rendered inside Settings' "workspace" section, right below IamWorkspaceHost
// (see settingsView.tsx) — not its own nav destination, since export is the
// only workspace-OPERATIONS row left.
//
// Composition: SettingsSection + SettingsRow + Button. Register:
// near-monochrome.

import { useLingui } from "@lingui/react/macro";
import { Button } from "../../Button/Button.js";
import { Link } from "../../Link/Link.js";
import { SettingsRow } from "../SettingsRow/SettingsRow.js";
import { SettingsSection } from "../SettingsSection/SettingsSection.js";

export type WorkspaceDataSectionProps = {
    readonly onExportWorkspaceLog: () => void;
};

export const WorkspaceDataSection = ({
    onExportWorkspaceLog,
}: WorkspaceDataSectionProps) => {
    const { t } = useLingui();
    return (
        <SettingsSection
            title={t`Workspace data`}
            description={t`Download a full copy of everything in this workspace.`}
        >
            <SettingsRow
                label={t`Download a copy`}
                description={t`Every change in this workspace, as one file.`}
                control={
                    <Button variant="secondary" onClick={onExportWorkspaceLog}>
                        {t`Download`}
                    </Button>
                }
            />
        </SettingsSection>
    );
};

// The operator's way into Ops (FR-12.2): feedback, the app's health, AI
// usage and accounts. Rendered only for admin accounts; Ops is its own page,
// so this is a plain link (`href` is caller-owned).
export type AdminAccountsSectionProps = {
    readonly href: string;
};

export const AdminAccountsSection = ({ href }: AdminAccountsSectionProps) => {
    const { t } = useLingui();
    return (
        <SettingsSection
            title={t`Administration`}
            description={t`Your account is an admin account.`}
        >
            <SettingsRow
                label={t`Ops`}
                description={t`Feedback from testers, the app's health, AI usage and accounts.`}
                control={<Link href={href}>{t`Open Ops`}</Link>}
            />
        </SettingsSection>
    );
};
